import * as React from 'react';
import cx from 'classnames';
import { size } from 'lodash';

import { ArrowLeftIcon, DownloadIcon } from 'src/icons';
import { IconButton } from 'src/widgets/Button';
import { Media } from 'src/widgets/Media';
import { Overlay } from 'src/widgets/Overlay';
import { ILicensedContent } from 'src/common/models/licensedContent';
import getFilenameFromUrl from 'src/common/utils/getFilenameFromUrl';
import { getDownloadableMediaURL } from 'src/common/utils/media';

import styles from './ContentOverlay.scss';

const { useState, useEffect } = React;

interface IProps {
  show: boolean;
  contents: ILicensedContent[];
  initialContentIndex: number;

  onRequestClose();
  onClickDownload(content: ILicensedContent);
}

const ContentOverlay: React.FunctionComponent<IProps> = React.memo((props) => {
  const {
    contents,
    initialContentIndex,
    onClickDownload,
    onRequestClose,
    show,
  } = props;

  const [contentIndex, setContentIndex] = useState<number>(initialContentIndex);

  useEffect(() => {
    if (show) {
      setContentIndex(initialContentIndex);
    } else {
      setTimeout(() => {
        setContentIndex(null);
      }, 400);
    }
  }, [show, initialContentIndex]);

  const currentContent = contents[contentIndex];

  const contentSize = size(contents);
  const handleNextPreview = () => setContentIndex((contentIndex + 1) % contentSize);
  const handlePreviousPreview = () =>
    setContentIndex(contentIndex - 1 < 0 ? contentSize - 1 : contentIndex - 1);

  const handleClickDownload = () => onClickDownload(currentContent);

  let fileName;
  if (currentContent) {
    const dlMediaUrl = getDownloadableMediaURL(currentContent);
    if (dlMediaUrl) {
      fileName = getFilenameFromUrl(dlMediaUrl);
    }
  }

  return (
    <Overlay
      show={show}
      onRequestClose={onRequestClose}
      className={styles.ContentOverlay}
      closeOnBackdropClick={true}
    >
      <div className={cx(styles.navigation, { [styles.hidden]: contentSize < 2 })}>
        <IconButton
          icon={<ArrowLeftIcon />}
          className={styles.leftArrow}
          onClick={handlePreviousPreview}
        />
        <IconButton
          icon={<ArrowLeftIcon />}
          className={styles.rightArrow}
          onClick={handleNextPreview}
        />
      </div>
      <Media content={currentContent} className={styles.content} />
      {fileName && (
        <>
          <div className={styles.fileName}>{fileName}</div>
          <div className={styles.download} onClick={handleClickDownload}>
            <DownloadIcon size={16} />
            <span>Download</span>
          </div>
        </>
      )}
    </Overlay>
  );
});

export default ContentOverlay;
